import React, { useMemo, useState } from "react";
import classNames from "classnames";
import { Button } from "@design-system/Button/Button";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { InstitutionIcon } from "@design-system/InstitutionIcon/InstitutionIcon";
// oxlint-disable-next-line no-restricted-imports -- Legacy migration debt — do not copy into new code.
import { Collapsible } from "@design-system/Collapsible/Collapsible";
import { ArrowCornerIcon, ArrowRightIcon } from "@icons";
import { useMcpConnectionsQuery } from "@api/mcpConnections.api";
import { useTypedFlags } from "@hooks/useTypedFlags";
import type { McpConnection } from "@common/schemas/domains/mcp/mcp-connection.schema";
import { McpConnectionsView } from "./McpConnectionsView";
import {
    CategoryFilter,
    ConnectorDefinition,
    connectorMatchesSearch,
    connectors,
    FALLBACK_CONNECTOR_ID,
    findConnectorByClientName,
    isConnectorEnabled,
} from "./connectorDefinitions";
import styles from "./McpCliSettings.module.scss";

const openExternal = (url: string) =>
    window.open(url, "_blank", "noopener,noreferrer");

const openConnectUrl = (url: string) => {
    if (url.startsWith("cursor://")) {
        window.location.href = url;
        return;
    }
    openExternal(url);
};

const ConnectorCard: React.FC<{
    connector: ConnectorDefinition;
    connections: McpConnection[];
}> = ({ connector, connections }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { connectUrl } = connector;

    return (
        <Collapsible
            className={styles.connectorCard}
            open={isOpen}
            onToggle={setIsOpen}
            actions={
                <>
                    {connectUrl && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={(event) => {
                                event.stopPropagation();
                                openConnectUrl(connectUrl);
                            }}
                        >
                            Connect
                        </Button>
                    )}
                    <ArrowRightIcon
                        className={classNames(styles.connectorChevron, {
                            [styles.connectorChevronOpen]: isOpen,
                        })}
                    />
                </>
            }
            header={
                <div className={styles.connectorInfo}>
                    <InstitutionIcon
                        institution={connector.institution}
                        size="lg"
                    />
                    <span className={styles.connectorName}>
                        {connector.name}
                    </span>
                </div>
            }
        >
            {(isOpened) =>
                isOpened && (
                    <>
                        {connections.length > 0 && (
                            <McpConnectionsView
                                data={connections}
                                className={styles.connectionsTable}
                            />
                        )}
                        <div className={styles.connectorActions}>
                            <a
                                className={styles.setupGuide}
                                href={connector.setupUrl}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Setup guide
                                <ArrowCornerIcon />
                            </a>
                        </div>
                    </>
                )
            }
        </Collapsible>
    );
};

const ConnectorSection: React.FC<{
    heading: string;
    sectionConnectors: ConnectorDefinition[];
    connectionsByConnectorId: Map<string, McpConnection[]>;
}> = ({ heading, sectionConnectors, connectionsByConnectorId }) => (
    <section className={styles.connectorSection}>
        <h4 className={styles.connectorSectionHeading}>{heading}</h4>
        <div className={styles.connectorList}>
            {sectionConnectors.map((connector) => (
                <ConnectorCard
                    key={connector.id}
                    connector={connector}
                    connections={
                        connectionsByConnectorId.get(connector.id) ?? []
                    }
                />
            ))}
        </div>
    </section>
);

interface ConnectorsCatalogProps {
    search: string;
    category: CategoryFilter;
}

export const ConnectorsCatalog: React.FC<ConnectorsCatalogProps> = ({
    search,
    category,
}) => {
    const { data: connections } = useMcpConnectionsQuery();
    const { googleSheetsConnector } = useTypedFlags();

    /**
     * Connections whose client maps to no connector, or to one that is hidden,
     * fall back to the Custom MCP card so they stay listed and revokable.
     */
    const connectionsByConnectorId = useMemo(() => {
        const grouped = new Map<string, McpConnection[]>();
        for (const connection of connections ?? []) {
            const connector = findConnectorByClientName(connection.clientName);
            const connectorId =
                connector &&
                isConnectorEnabled(connector, { googleSheetsConnector })
                    ? connector.id
                    : FALLBACK_CONNECTOR_ID;
            const connectorConnections = grouped.get(connectorId) ?? [];
            connectorConnections.push(connection);
            grouped.set(connectorId, connectorConnections);
        }
        return grouped;
    }, [connections, googleSheetsConnector]);

    const visibleConnectors = useMemo(
        () =>
            connectors.filter((connector) => {
                if (!isConnectorEnabled(connector, { googleSheetsConnector })) {
                    return false;
                }
                const matchesCategory =
                    category === "all" || connector.category === category;
                return (
                    matchesCategory && connectorMatchesSearch(connector, search)
                );
            }),
        [search, category, googleSheetsConnector],
    );

    const connectedConnectors = visibleConnectors.filter((connector) =>
        connectionsByConnectorId.has(connector.id),
    );
    const exploreConnectors = visibleConnectors.filter(
        (connector) => !connectionsByConnectorId.has(connector.id),
    );

    if (visibleConnectors.length === 0) {
        return (
            <div className={styles.connectors}>
                <EmptyState header="No connectors match your search" />
            </div>
        );
    }

    return (
        <div className={styles.connectors}>
            {connectedConnectors.length > 0 && (
                <ConnectorSection
                    heading="My connectors"
                    sectionConnectors={connectedConnectors}
                    connectionsByConnectorId={connectionsByConnectorId}
                />
            )}
            {exploreConnectors.length > 0 && (
                <ConnectorSection
                    heading="Explore"
                    sectionConnectors={exploreConnectors}
                    connectionsByConnectorId={connectionsByConnectorId}
                />
            )}
        </div>
    );
};
